import { create } from 'zustand';
import type { Card, ProposedLand, LandCategory, Color } from '@mtg-deck-tool/shared';
import type { LandDiffResult, KarstenSourceRow, ManaOverviewRow } from '../lib/land-sim/karsten-types.js';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A resolved land card ready for display and acceptance. */
export interface ResolvedProposalEntry {
  card: Card;
  quantity: number;
  category: LandCategory;
  /** True if this card was already in the deck (shown grayed out). */
  alreadyInDeck: boolean;
}

/** The pending land proposal state. Null when no proposal is pending. */
export interface PendingProposal {
  /** The raw proposal list (used for accept logic — quantities for basics etc). */
  rawProposal: ProposedLand[];
  /** Resolved Card objects grouped with quantity and category for display. */
  resolvedEntries: ResolvedProposalEntry[];
  /** The generation mode ('generate' | 'complete') at time of proposal. */
  mode: 'generate' | 'complete';
  /** The deck's color identity at the time the proposal was generated. */
  deckColorIdentity: Color[];

  /** Snapshot of deck land names+quantities at Generate click time. Undefined = blank deck. */
  beforeSnapshot?: Map<string, number>;
  /** Pre-computed Karsten source panel rows. */
  karstenRows?: KarstenSourceRow[];
  /** Pre-computed mana overview panel rows. */
  manaOverviewRows?: ManaOverviewRow[];
  /** Pre-computed land diff. Undefined = no diff to show (blank deck before generation). */
  landDiff?: LandDiffResult;
}

// ─── Store Interface ──────────────────────────────────────────────────────────

interface LandProposalState {
  pendingProposal: PendingProposal | null;

  /**
   * Store a pending proposal after the modal generates it.
   */
  setPendingProposal: (proposal: PendingProposal) => void;

  /**
   * Clear the pending proposal (after accept or cancel).
   */
  clearPendingProposal: () => void;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useLandProposalStore = create<LandProposalState>()((set) => ({
  pendingProposal: null,

  setPendingProposal: (proposal) => set({ pendingProposal: proposal }),

  clearPendingProposal: () => set({ pendingProposal: null }),
}));
